<template>
  <div class="offer-position-boilerplates">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getOfferPositionBoilerplates"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getOfferPositionBoilerplateCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('offerPositionBoilerplates') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>

            <button
              class="btn btn-sm btn-inverse"
              @click="$emit('create-offer-position-boilerplate')"
            >
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('offerPositionBoilerplateCreate') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Number -->
      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center; width: 60px'"
        filterField="number"
      >
        <template #body="slotProps">
          <router-link
            v-if="slotProps.data && slotProps.data.number !== null"
            :to="{
              name: 'OfferPositionBoilerplateEditPage',
              params: { offerPositionBoilerplateNumber: slotProps.data.number },
            }"
          >
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </router-link>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Name -->
      <Column field="name" :header="$t('name')" sortable filterField="name">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.name">
            {{ slotProps.data.name }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Description -->
      <Column field="description" :header="$t('description')" sortable filterField="description">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.description">
            {{ slotProps.data.description }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';

export default {
  name: 'OfferPositionBoilerplatesTable',
  components: {
    DataTable,
    Column,
    InputText,
  },
  data() {
    return {
      tableStateConfig: {
        name: 'offer-position-boilerplates',
        filters: {
          number: { value: null },
          name: { value: null },
          description: { value: null },
        },
        customFilters: {
          //   offerBoilerplates: [],
        },
        sorting: { sortField: 'number', sortOrder: -1 }, // Latest orders first
      },
      defaultFilters: {
        number: { value: null },
        name: { value: null },
        description: { value: null },
      },
      defaultCustomFilters: {
        // offerBoilerplates: [],
      },
    };
  },
  computed: {
    ...mapGetters(['getOfferPositionBoilerplates', 'getOfferPositionBoilerplateCount']),
    ...mapGetters(['isLoading']),
    ...mapGetters(['getTableState']),

    tableStateStore() {
      return this.getTableState(this.tableStateConfig.name);
    },
  },

  async created() {
    await this.initTableState(this.tableStateConfig);
  },

  async mounted() {
    await this.loadTableData();
  },
  methods: {
    ...mapActions(['fetchOfferPositionBoilerplates']),
    ...mapActions(['initTableState']),
    async loadTableData() {
      try {
        await this.fetchOfferPositionBoilerplates({
          pagination: {
            skip: this.tableStateStore.skip(),
            pageSize: this.tableStateStore.pagination.pageSize,
          },
          sorting: this.tableStateStore.sorting,
          filters: this.tableStateStore.filters,
        });
      } catch (error) {
        console.error('Error loading offer position boilerplates:', error);
      }
    },
    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },
    /**
     * Reset filter settings
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },
};
</script>

<style lang="scss" scoped>
.offer-position-boilerplates {
  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    .table-header-left {
      .page-title {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
      }
    }

    .table-header-right {
      display: flex;
      gap: 0.5rem;
    }
  }
}
/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}
</style>
